// Workspace-tab content shape for the navigational-features ReferencesTab
// (Phase 3 — Slice 24).
//
// Lives next to the tab component so the workspace registry can import the
// content type without pulling React. The shape is intentionally minimal —
// the tab fetches object bodies on mount to render code-snippet previews.

import type { DatabaseEngineId } from '@/core/engines';

import type {
  FindReferencesResultGroup,
  IdentifierKind,
  QualifiedName,
} from '../types';

export type ReferencesTabStatus = 'idle' | 'loading' | 'ready' | 'error';

export type ReferencesTabContent = {
  readonly connectionId: string;
  readonly database: string;
  readonly engineId: DatabaseEngineId;
  readonly identifier: QualifiedName;
  readonly identifierKind: IdentifierKind;
  /** Source object that hosts the identifier (the right-clicked column's
   *  table/view). Used to label the tab and for click-through routing on
   *  table-resident references like default/check constraints. */
  readonly relation?: QualifiedName;
  readonly groups: readonly FindReferencesResultGroup[];
  /** Per-object body text keyed by {@link bodyKey}. Filled lazily once the tab
   *  mounts; missing entries render the reference with no preview snippet. */
  readonly bodies: Readonly<Record<string, string>>;
  readonly status: ReferencesTabStatus;
  /** Mirrors `FindReferencesResult.partial` — the identifier index was built
   *  from only part of the connection's bodies, so this list may be
   *  incomplete. Rendered as a note next to the reference count. */
  readonly partial?: boolean;
  readonly error?: string;
};

/** The per-group body-cache key and React key. Uses each group's EXACT
 *  spelling: a group is already one object under the index's own key policy,
 *  and on a case-sensitive catalog (a SQL Server `_CS_` database, MySQL with
 *  `lower_case_table_names = 0`, PostgreSQL, Oracle) `dbo.V` and `dbo.v` are
 *  two of them. A folded key made the second group reuse the first's cached
 *  body and render the wrong snippet. */
export function bodyKey(
  objectKind: string,
  objectName: QualifiedName,
): string {
  return `${objectKind}::${objectName.schema ?? ''}::${objectName.name ?? ''}`;
}
